import { Link } from "react-router-dom";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { SeoHead } from "@/components/SeoHead";
import { WebPageJsonLd } from "@/components/marketing/WebPageJsonLd";

const EFFECTIVE_DATE = "May 25, 2026";

export default function Terms() {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Terms of Service — Excerptus"
        description="Excerptus Terms of Service. Operated by Knowledge Worker LLC. 10-day free trial on every plan, no refunds, governed by Ohio law."
      />
      <WebPageJsonLd
        name="Terms of Service — Excerptus"
        description="Excerptus Terms of Service. Operated by Knowledge Worker LLC."
        path="/terms/"
        dateModified="2026-05-25"
      />
      <MarketingNav />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <article className="prose-legal">
          <h1 className="font-serif text-4xl font-semibold text-foreground">Terms of Service</h1>
          <p className="mt-2 text-sm text-muted-foreground">Effective: {EFFECTIVE_DATE}</p>

          <Section title="1. Who we are">
            Excerptus is operated by Knowledge Worker LLC ("Excerptus", "we", "us"). You can reach us at{" "}
            <a className="text-primary underline" href="mailto:accounts@excerptus.io">accounts@excerptus.io</a>.
          </Section>

          <Section title="2. Your account">
            You must be at least 13 years old to use Excerptus. You're responsible for keeping your
            password secure and for everything that happens under your account. Tell us right away if
            you suspect unauthorized access.
          </Section>

          <Section title="3. Acceptable use">
            You agree not to: (a) upload content you don't have the right to use; (b) attempt to break,
            probe, or overload the service; (c) resell or sublicense the service; (d) use Excerptus to
            store unlawful, harmful, or harassing material. We may suspend accounts that violate these
            rules.
          </Section>

          <Section title="4. Your content">
            You keep ownership of the excerpts, notes, library items, drafts, and images you
            save. By using Excerptus, you grant us a limited license to store, display, and process that
            content solely so we can provide the service to you. We do not use your content to train AI
            models — ours or anyone else's. You're responsible for making sure your use of third-party
            text or images (book passages, articles, etc.) is permitted under applicable copyright law
            and fair use.
          </Section>

          <Section title="5. Subscriptions and payments">
            Excerptus is a paid service offered in five tiers — Starter, Plus, Pro, Elite, and Agency —
            billed monthly or yearly through Stripe. Prices are shown on our{" "}
            <Link className="text-primary underline" to="/pricing/">Pricing page</Link>. Every plan
            includes a 10-day free trial. We collect a payment method at signup to confirm you're a real
            person, but nothing is charged until the trial ends. Subscriptions renew automatically until
            canceled. You can cancel anytime from your account Settings — your access continues through
            the end of the period you've already paid for.
          </Section>

          <Section title="6. Refunds">
            All payments are final. We do not offer refunds for partial months, unused periods, or
            downgrades. The 10-day free trial on every plan is your opportunity to confirm Excerptus is
            the right fit — cancel anytime during the trial and you won't be charged. After the trial,
            you can cancel anytime and keep access through the end of your current billing period.
          </Section>

          <Section title="7. Plan limits and changes">
            Each plan includes specific limits and features as described on the{" "}
            <Link className="text-primary underline" to="/pricing/">Pricing page</Link> at the time of
            purchase — including excerpt counts, image downloads, workspace counts, publishing
            connectors, and AI access. We may adjust these limits with reasonable notice. If we make a
            material change that reduces the value of a paid plan, we'll notify you by email and, where
            required, offer a prorated refund of unused time.
          </Section>

          <Section title="8. Third-party integrations">
            Excerptus integrates with third-party services you choose to connect — including Readwise,
            publishing destinations (Kit, Bento, WordPress, and others), and bring-your-own-key AI and
            image providers (such as Fal.ai for the upcoming Post Image generator on Pro and above). Your use of those
            services is governed by their own terms and privacy policies. You're responsible for any
            usage fees those providers charge against your own account or API key.
          </Section>

          <Section title="9. Service changes">
            We may add, change, or remove features over time. Material reductions to paid features will
            be communicated by email or in-app notice in advance.
          </Section>

          <Section title="10. Termination">
            You can request account closure at any time by emailing us. We may suspend or terminate
            accounts that violate these terms or that pose a risk to the service or other users. On
            termination, we'll delete your data per the Privacy Policy, while retaining billing records
            only as required by law.
          </Section>

          <Section title="11. Disclaimers">
            Excerptus is provided "as is" without warranties of any kind, express or implied. We don't
            guarantee the service will be uninterrupted, error-free, or that data will never be lost.
            Keep your own backups of anything important — you can export your workspace data as JSON
            from Settings at any time.
          </Section>

          <Section title="12. Limitation of liability">
            To the maximum extent permitted by law, Knowledge Worker LLC's total liability for any claim
            arising from your use of Excerptus is limited to the amount you paid us in the 12 months
            before the claim. We're not liable for indirect, incidental, or consequential damages.
          </Section>

          <Section title="13. Governing law">
            These terms are governed by the laws of the State of Ohio, United States, without regard
            to conflict-of-laws rules. Any dispute will be resolved in the state or federal courts
            located in Ashtabula County, Ohio.
          </Section>

          <Section title="14. Prohibited uses">
            In addition to the general principles in §3, you agree not to use Excerptus to create,
            store, process, publish, or distribute any of the following:
            <br /><br />
            (a) <strong>Illegal activity.</strong> Anything that violates applicable law, including
            fraud, money laundering, sanctions evasion, or unauthorized practice of a regulated
            profession.
            <br /><br />
            (b) <strong>Harmful content.</strong> Content that sexually exploits or endangers minors
            (including any form of CSAM), promotes or facilitates self-harm or suicide, glorifies or
            incites violence, or constitutes credible threats against any person or group.
            <br /><br />
            (c) <strong>Security compromise.</strong> Attempts to probe, scan, reverse-engineer,
            penetrate, or otherwise compromise the service, our infrastructure, or any other user's
            account or data.
            <br /><br />
            (d) <strong>Abuse of infrastructure.</strong> Sending spam, mass unsolicited messages,
            phishing, malware, or using Excerptus as a relay for bulk email, scraping, or
            denial-of-service activity against any third party.
            <br /><br />
            (e) <strong>Reselling the service.</strong> Reselling, sublicensing, white-labeling, or
            otherwise repackaging Excerptus or its outputs as a competing service, except as
            expressly permitted by your plan (for example, Agency client workspaces).
            <br /><br />
            (f) <strong>AI misuse.</strong> Using Excerptus's AI features, or AI keys connected
            through Excerptus, to generate content that is defamatory, deceptive (including
            impersonation or synthetic media intended to mislead), non-consensual intimate imagery,
            CSAM, malware, or that violates third-party intellectual property rights.
            <br /><br />
            (g) <strong>Publishing and integration misuse.</strong> Using connected publishing
            destinations, Readwise sync, or other integrations to distribute content that violates
            those providers' terms or this section.
            <br /><br />
            (h) <strong>Tampering with the service.</strong> Circumventing plan limits, usage caps,
            credit accounting, rate limits, watermarks, attribution, or any access controls;
            interfering with billing or audit records.
            <br /><br />
            (i) <strong>Identity misrepresentation.</strong> Impersonating another person or
            organization, falsely claiming affiliation, or creating accounts in someone else's name
            without their permission.
            <br /><br />
            (j) <strong>Trial, billing, and affiliate abuse.</strong> Creating multiple accounts to
            re-trigger the free trial, chargeback fraud, fraudulent affiliate referrals (self-referral,
            fake traffic, coupon/cookie stuffing, or any scheme designed to manufacture commissions),
            or any other circumvention of the billing or affiliate systems.
            <br /><br />
            (k) <strong>Plagiarism and academic fraud.</strong> Using Excerptus to plagiarize, to strip
            or falsify authorship or citation of third-party work, to ghost-produce academic
            submissions in violation of institutional rules, or to otherwise misrepresent the
            authorship or provenance of content. Excerptus is built to make honest citation easier —
            using it to make dishonest citation easier is a material breach of these terms.
            <br /><br />
            (l) <strong>Hate, extremism, pornography, and conspiracy content.</strong> Content that
            promotes hatred or discrimination against any protected class; pornographic or sexually
            explicit material; religious or political extremism, fundamentalism that promotes
            coercion, supremacy, or the subjugation of others, radicalization, terrorism, or
            paramilitary recruitment; or the promotion of conspiracy theories presented as fact. Good-
            faith journalism, scholarship, criticism, historical analysis, or fiction that examines
            these subjects is not prohibited by this clause.
            <br /><br />
            (m) <strong>AI-only content operations.</strong> Operating Excerptus as an unattended,
            fully-automated AI content farm — that is, pipelines where AI generates, edits, and
            publishes content end-to-end without a human author reviewing and taking responsibility
            for what ships. Excerptus is designed for human Knowledge Workers using AI as an
            assistant, not as a replacement. This restriction applies equally to participants in the
            Excerptus affiliate program: affiliate accounts may not refer traffic from, or be
            promoted by, AI-only content farms or auto-generated sites.
            <br /><br />
            <strong>Enforcement.</strong> We may suspend or terminate any account that violates this
            section, with or without notice and without refund. For severe violations — including
            CSAM, credible threats of violence, terrorism or material support thereof, large-scale
            plagiarism schemes, or coordinated extremism or disinformation operations — we will
            terminate immediately and may refer the matter to law enforcement or other appropriate
            authorities. We reserve all other rights and remedies available under law.
          </Section>

          <Section title="15. Changes to these terms">
            We may update these terms from time to time. If we make material changes, we'll notify you by
            email or in-app notice. Continued use of Excerptus after the effective date means you accept
            the updated terms.
          </Section>

          <Section title="16. Contact">
            Questions about these terms? Email{" "}
            <a className="text-primary underline" href="mailto:accounts@excerptus.io">
              accounts@excerptus.io
            </a>.
          </Section>

        </article>
      </main>
      <MarketingFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="font-serif text-xl font-semibold text-foreground">{title}</h2>
      <p className="mt-2 text-base leading-relaxed text-muted-foreground">{children}</p>
    </section>
  );
}
